<template>
    <div class="editor">
        <div v-if="pageId" class="editor__content" :style="containerStyles">
            <page :page-id="pageId" :screen="screen" />
        </div>

        <favorites v-if="showFavorites" />
        <toolbar-editor :page-id="pageId" :screen="screen" />
        <loading-page :visible-default="loading" />
        <snackbar />
    </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex';

import Favorites from '@/components/page-editor/favorites';
import ToolbarEditor from '@/components/shared/toolbars/toolbar-editor';
import Page from '@/components/page-editor/page';
import LoadingPage from '@/components/shared/loading/loading-page';
import { LOAD_SETTINGS } from '@/store/types/action-types';
import {
    MOUNT_FAVORITES,
    SET_PAGE_DATA,
    SET_RENDERINGS,
    SHOW_DRAWER,
    TOGGLE_TOOLBAR,
} from '@/store/types/mutation-types';
import listenOnRootMixin from '@/mixins/listen-on-root';

export default {
    name: 'page-editor',

    components: {
        Favorites,
        LoadingPage,
        Page,
        ToolbarEditor,
    },

    mixins: [listenOnRootMixin],

    props: {
        pageId: {
            type: String,
            default: '',
        },

        screen: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            toolbars: {},
            loading: true,
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
            toolbarVisibility: state => state.page.toolbarVisibility,
            lastPageId: state => state.page.lastPageId,
        }),

        containerStyles() {
            if (this.previewMode || !this.toolbars.editor) {
                return {
                    width: '100%',
                };
            }

            const { collapseOffset, width } = this.toolbars.editor;

            return {
                width: this.toolbarVisibility.editor
                    ? `calc(100% - ${ width }px)`
                    : `calc(100% - ${ collapseOffset }px)`,
            };
        },

        showFavorites() {
            return this.$route.name === 'editor' && !this.pageId;
        },
    },

    created() {
        this.loadSettings();
        this.listenOnRoot('flex::toolbar::update', this.cacheToolbarDimensions);
        this.toggleToolbar({ id: 'editor', isVisible: true });

        if (!this.pageId) {
            this.loading = false;
        }
    },

    mounted() {
        this.mountFavorites();

        if (this.$route.name === 'templates' && !this.$route.params.pageId) {
            this.showDrawer('load-template-drawer');
        }
    },

    methods: {
        ...mapActions({
            loadSettings: LOAD_SETTINGS,
        }),

        ...mapMutations({
            mountFavorites: MOUNT_FAVORITES,
            setPageData: SET_PAGE_DATA,
            setRenderings: SET_RENDERINGS,
            showDrawer: SHOW_DRAWER,
            toggleToolbar: TOGGLE_TOOLBAR,
        }),

        cacheToolbarDimensions({ id, dimensions }) {
            if (!this.toolbars[id]) {
                this.$set(this.toolbars, id, {});
            }

            this.toolbars[id] = { ...dimensions };
        },
    },
};
</script>

<style lang="scss">
.editor {
    display: flex;
    align-items: flex-start;
    height: 100%;
    box-shadow: $header-box-shadow;
    color: inherit;
    background-color: inherit;

    &__content {
        width: 100%;
        height: 100%;
        transition: padding 0.33s;
        padding-left: 20px;
        padding-right: 20px;
        z-index: 50;

        .#{ $is-preview } & {
            padding: 0;
        }

        .#{ $is-editing } & {
            color: var(--artboard-color);
            background-color: var(--artboard-backgroundColor);
        }
    }
}
</style>
